import { APISERVICE } from '@util/apiService/RestClient';
import { windowRef } from '@util/window';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { navigateTo } from '@util/routerService';
import { getOrderByOrderId } from '@storeData/order';

export interface PostCarePageProps {
  propServiceId?: string;
  propServiceName?: string;
  isPopup?: boolean;
  onClose?: () => void;
}

export function PostCarePage({ propServiceId, propServiceName, isPopup, onClose }: PostCarePageProps) {
  const router = useRouter();
  const storeMetaData = useSelector((state: any) => state.store ? state.store.storeMetaData : null);
  const storeData = useSelector((state: any) => state.store ? state.store.storeData : null);
  const isCatalogueEnabled = Boolean(storeData?.configData?.storeConfig?.basicConfig?.catalogue);
  
  const [loading, setLoading] = useState<boolean>(true);
  const [postCare, setPostCare] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [loadedImages, setLoadedImages] = useState<Record<number, boolean>>({});

  const handleImageLoad = (idx: number) => {
    setLoadedImages(prev => ({ ...prev, [idx]: true }));
  };

  const getServiceIdFromUrl = () => {
    if (propServiceId) return propServiceId;
    if (router?.query?.serviceId) return router.query.serviceId;
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('serviceId');
    }
    return null;
  };

  const getServiceNameFromUrl = () => {
    if (propServiceName) return propServiceName;
    if (router?.query?.serviceName) return router.query.serviceName;
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.get('serviceName');
    }
    return null;
  };

  const findServiceNameInCategories = (categories: any[], targetServiceId: string): string | null => {
    if (!categories || !Array.isArray(categories)) return null;
    for (const cat of categories) {
      if (cat?.itemList && Array.isArray(cat.itemList)) {
        for (const item of cat.itemList) {
          if (
            String(item?.id) === String(targetServiceId) ||
            String(item?.serviceId) === String(targetServiceId)
          ) {
            return item.name || null;
          }
        }
      }
      if (cat?.categoryList && Array.isArray(cat.categoryList)) {
        const found = findServiceNameInCategories(cat.categoryList, targetServiceId);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    if (windowRef && !isPopup) window.scrollTo(0, 0);
  }, [windowRef, router.asPath, isPopup]);

  useEffect(() => {
    const serviceId = getServiceIdFromUrl();
    if (!serviceId || !storeMetaData?.id || !storeMetaData?.tenantId) return;

    setLoading(true);
    setErrorMsg('');
    const url = `${process.env.NEXT_PUBLIC_GET_BASE_CATALOG_URL}/public/postCare/service?tenantId=${storeMetaData.tenantId}&storeId=${storeMetaData.id}&serviceId=${serviceId}`;

      APISERVICE.GET(url)
        .then((resp: any) => {
          const data = resp?.data?.data || resp?.data || null;
          if (data && (data.richTextHtml || data.imageUrl || (data.imageUrls && data.imageUrls.length > 0) || (data.files && data.files.length > 0))) {
            // Normalize image list
            let images: string[] = [];
            if (Array.isArray(data.files) && data.files.length > 0) {
              images = data.files.map((file: any) => file?.filePath).filter(Boolean);
            } else if (Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
              images = data.imageUrls;
            } else if (data.imageUrl) {
              images = [data.imageUrl];
            }
            data.normalizedImages = images;
            setPostCare(data);
          } else {
            setPostCare(null);
          }
          setLoading(false);
      })
      .catch((err: any) => {
        console.error('Error fetching postcare:', err);
        setErrorMsg('Failed to fetch post-care instructions.');
        setLoading(false);
      });
  }, [router.asPath, router.query.serviceId, storeMetaData, propServiceId]);

  const serviceId = getServiceIdFromUrl() || '';
  const serviceNameFromUrl = getServiceNameFromUrl();
  const serviceNameFromStore = serviceId
    ? findServiceNameInCategories(storeData?.categories, String(serviceId))
    : null;
  const resolvedServiceName =
    serviceNameFromUrl || serviceNameFromStore || postCare?.serviceName || 'Service';

  const hasText = Boolean(
    postCare?.richTextHtml &&
      postCare.richTextHtml.trim() !== '<p></p>' &&
      postCare.richTextHtml.trim() !== ''
  );
  
  const images = postCare?.normalizedImages || [];
  const hasImages = images.length > 0;

  return (
    <div className={`postcare-wrap ${isPopup ? 'is-popup' : ''}`}>
      <div className="postcare-card">
        {!isPopup && (
            <div className="postcare-header">
            <h2 className="postcare-title">
                <span>PostCare - {resolvedServiceName}</span>
            </h2>
            </div>
        )}

        {loading ? (
          <div className="postcare-loading-container">
            <div className="loading-icon">⏳</div>
            <p>Loading clinical post-care instructions...</p>
          </div>
        ) : errorMsg ? (
          <div className="postcare-empty-state">
            <div className="empty-icon">⚠️</div>
            <h3>Unable to load instructions</h3>
            <p>{errorMsg}</p>
            {!isPopup && isCatalogueEnabled && (
              <button
                type="button"
                className="postcare-back-btn"
                onClick={() => navigateTo('home', storeData?.baseRouteUrl)}
              >
                ← Home
              </button>
            )}
          </div>
        ) : hasText || hasImages ? (
          <>
            {hasText && (
              <div
                className={`postcare-html-content ${hasImages ? 'has-images' : ''}`}
                dangerouslySetInnerHTML={{ __html: postCare.richTextHtml }}
              />
            )}

            {hasImages && (
              <div className="postcare-images-section">
                {hasText && <h3 className="images-heading">Reference Images</h3>}
                <div className="images-list">
                    {images.map((img: string, idx: number) => (
                        <div 
                            key={idx} 
                            className={`postcare-image-container zoomable-image-container ${loadedImages[idx] ? 'loaded' : 'loading'}`}
                            onClick={() => {
                                if (loadedImages[idx]) setZoomedImage(img);
                            }}
                        >
                            {!loadedImages[idx] && (
                                <div className="image-loading-placeholder">
                                    <div className="image-loading-icon">⏳</div>
                                    <div className="image-loading-text">Loading image...</div>
                                </div>
                            )}
                            <img
                                src={img}
                                alt={`Post-Care Reference ${idx + 1}`}
                                onLoad={() => handleImageLoad(idx)}
                                className={`postcare-img ${loadedImages[idx] ? 'is-loaded' : ''}`}
                            />
                        </div>
                    ))}
                </div>
              </div>
            )}

            {zoomedImage && (
                <div 
                    className="zoom-lightbox-overlay"
                    onClick={() => setZoomedImage(null)}
                >
                    <img 
                        src={zoomedImage} 
                        className="zoom-lightbox-img" 
                        alt="Zoomed Reference" 
                    />
                    <div className="zoom-lightbox-close">
                        ×
                    </div>
                </div>
            )}

            {!isPopup && isCatalogueEnabled && (
                <div className="postcare-back-container">
                <button
                    type="button"
                    className="postcare-back-btn"
                    onClick={() => navigateTo('home', storeData?.baseRouteUrl)}
                >
                    ← Home
                </button>
                </div>
            )}
          </>
        ) : (
          <div className="postcare-empty-state">
            <div className="empty-icon">📋</div>
            <h3>No Instructions Found</h3>
            <p>
              There are currently no specific post-care instructions or prescription notes published for this service.
            </p>
            {!isPopup && isCatalogueEnabled && (
                <button
                type="button"
                className="postcare-back-btn"
                onClick={() => navigateTo('home', storeData?.baseRouteUrl)}
                >
                ← Home
                </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderPostCareList({ orderId, ...props }: any) {
  const storeMetaData = useSelector((state: any) => state.store ? state.store.storeMetaData : null);
  const storeData = useSelector((state: any) => state.store ? state.store.storeData : null);
  const isCatalogueEnabled = Boolean(storeData?.configData?.storeConfig?.basicConfig?.catalogue);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState<{id: string, name: string}[]>([]);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!orderId || !storeMetaData?.id) return;
    setLoading(true);
    getOrderByOrderId(orderId, storeMetaData.id, storeMetaData.tenantId)
      .then((order: any) => {
        const extractedServices: {id: string, name: string}[] = [];
        if (order?.products) {
          order.products.forEach((item: any) => {
            if ((item?.type?.toLowerCase() === 'service' || order?.type?.toLowerCase() === 'appointment') && item.id) {
               extractedServices.push({ id: item.id, name: item.name });
            }
          });
        } else if (order?.expertAppointments) {
          order.expertAppointments.forEach((item: any) => {
            if (item.serviceId) {
              extractedServices.push({ id: item.serviceId, name: item.service });
            }
          });
        }
        
        // Remove duplicates if any
        const uniqueServices = Array.from(new Map(extractedServices.map(item => [item.id, item])).values());
        setServices(uniqueServices);
        setLoading(false);
      })
      .catch(err => {
        setErrorMsg('Failed to load order details');
        setLoading(false);
      });
  }, [orderId, storeMetaData]);

  if (loading) {
     return <div className="postcare-loading-container order-loading"><div className="loading-icon">⏳</div><p>Loading order instructions...</p></div>;
  }

  if (errorMsg || services.length === 0) {
     return (
       <div className="postcare-empty-state order-empty">
         <div className="empty-icon">⚠️</div>
         <h3>No Instructions Found</h3>
         <p>{errorMsg || 'No services found in this order.'}</p>
         {isCatalogueEnabled && (
           <button type="button" className="postcare-back-btn" onClick={() => navigateTo('home', storeData?.baseRouteUrl)}>← Home</button>
         )}
       </div>
     );
  }

  return (
    <div className="postcare-wrap">
      <div className="postcare-header">
        <h2 className="postcare-title">
            <span>Post-Care Instructions</span>
        </h2>
      </div>
      <div className="postcare-order-list">
        {services.map((s, idx) => (
          <div key={idx} className="postcare-order-item">
            <h3 className="postcare-order-item-title">{s.name}</h3>
            <PostCarePage propServiceId={s.id} propServiceName={s.name} isPopup={true} />
          </div>
        ))}
      </div>
      {isCatalogueEnabled && (
        <div className="postcare-order-footer">
          <button type="button" className="postcare-back-btn" onClick={() => navigateTo('home', storeData?.baseRouteUrl)}>← Home</button>
        </div>
      )}
    </div>
  );
}

export default function PostCarePageWrapper(props: PostCarePageProps) {
  const router = useRouter();
  const orderId = router?.query?.orderId || (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('orderId') : null);

  if (orderId) {
    return <OrderPostCareList orderId={orderId} {...props} />;
  }

  return <PostCarePage {...props} />;
}
